import * as THREE from "three";
import { CameraFollow, ScreenBox } from "./cameraFollow";

/**
 * The level camera's framing (isoCamera.ts): the UI insets the map is framed within, eased as panels change size, and
 * the placement follow — where the view must go to frame some points, and the spring that takes it there. The spring
 * and the fit rules are cameraFollow.ts; this applies them to the camera through a FramingHost.
 */

/**
 * The pixels covered by UI at the top and bottom of the viewport. `top`/`bottom` are as last reported: where the band
 * between them will be once the view has caught up, so placement framing (`frameTarget`) aims for it. `framedTop`/
 * `framedBottom` are the insets the map is actually centred and clamped within this frame: `ease` lets them trail the
 * reported ones on a critically damped spring, so a panel changing size (the card tray dealt, morphed into the
 * placement bar, put away; the tower sheet) slides the view instead of snapping it (Kevin, 2026-09-23: "as the
 * pick-a-card screen shows, the game jumps up / back down").
 */
export class InsetEase {
  top = 0;
  bottom = 0;
  framedTop = 0;
  framedBottom = 0;
  private vTop = 0;
  private vBottom = 0;
  /** The insets the framed ones are easing to: the reported ones, once they have held for `HoldSeconds`. */
  private goalTop = 0;
  private goalBottom = 0;
  /** How long the reported insets have differed from the goal, in seconds. */
  private reportedFor = 0;
  /**
   * A reported size must hold this long (seconds, a few frames) before the view starts to move for it, so a size a
   * panel passes through for a layout or two — between two hands, mid-morph — never nudges the view at all.
   */
  static readonly HoldSeconds = 0.06;
  /**
   * Inset spring time constant, in seconds: within 1 % of a new inset after ~3.3 × this, i.e. ~280 ms — long enough
   * to read as a glide, short enough that the map is out from under a new panel before the eye settles on it.
   */
  static readonly SmoothTime = 0.085;
  /** An eased inset this close (CSS pixels, and px/s) to the reported one lands on it, so the spring stops. */
  static readonly SettlePx = 0.25;

  /** New insets applied at once (framing a new view). */
  set(top: number, bottom: number): void {
    this.top = this.goalTop = this.framedTop = Math.max(0, top);
    this.bottom = this.goalBottom = this.framedBottom = Math.max(0, bottom);
    this.vTop = this.vBottom = 0;
    this.reportedFor = 0;
  }

  /** New insets on a live view: the framed ones ease to them in `step` (after the hold). */
  ease(top: number, bottom: number): void {
    top = Math.max(0, top); bottom = Math.max(0, bottom);
    if (top === this.top && bottom === this.bottom) return;
    this.top = top; this.bottom = bottom;
    this.reportedFor = 0;
  }

  /**
   * One spring step of the framed insets towards the reported ones. Whether they are still under way — moving, or a
   * new report being held — so a caller that renders only while the camera is busy keeps ticking through the hold.
   */
  step(dt: number): boolean {
    const held = this.goalTop !== this.top || this.goalBottom !== this.bottom;
    if (held) {
      this.reportedFor += dt;
      if (this.reportedFor >= InsetEase.HoldSeconds) { this.goalTop = this.top; this.goalBottom = this.bottom; }
    }
    if (this.framedTop === this.goalTop && this.framedBottom === this.goalBottom) return held;
    const st = InsetEase.SmoothTime, near = InsetEase.SettlePx;
    const t = CameraFollow.damp(this.framedTop, this.goalTop, this.vTop, st, dt);
    const b = CameraFollow.damp(this.framedBottom, this.goalBottom, this.vBottom, st, dt);
    this.framedTop = t.value; this.vTop = t.velocity;
    this.framedBottom = b.value; this.vBottom = b.velocity;
    if (Math.abs(this.framedTop - this.goalTop) < near && Math.abs(this.vTop) < near) { this.framedTop = this.goalTop; this.vTop = 0; }
    if (Math.abs(this.framedBottom - this.goalBottom) < near && Math.abs(this.vBottom) < near) { this.framedBottom = this.goalBottom; this.vBottom = 0; }
    return true;
  }
}

/** A placement glide under way: its target, the spring's time and velocities, and how long it has run (s). */
export interface Follow { x: number; z: number; zoom: number; smoothTime: number; vx: number; vz: number; vZoom: number; age: number }

/** What the framing moves and reads: IsoCamera's state, through accessors (isoCamera.ts `framing`). */
export interface FramingHost {
  readonly camera: THREE.PerspectiveCamera;
  readonly width: number;
  readonly height: number;
  readonly centre: THREE.Vector3;
  readonly pan: THREE.Vector3;
  zoom: number;
  readonly minZoom: number;
  readonly maxZoom: number;
  readonly insets: InsetEase;
  /** Places the camera at the pan and zoom, clamped within the band these insets leave (the framed ones by default). */
  apply(top?: number, bottom?: number): void;
  groundAt(px: number, py: number): THREE.Vector3 | null;
}

/** Project-and-correct rounds in `frameTarget`: the error shrinks by an order of magnitude a round. */
export const FollowFitRounds = 4;

const clampZoom = (h: FramingHost, z: number) => Math.min(h.maxZoom, Math.max(h.minZoom, z));
const lookOf = (h: FramingHost) => ({ x: h.centre.x + h.pan.x, z: h.centre.z + h.pan.z });

/**
 * Where the look point and zoom must go for `points` (world positions) to sit in the band of the screen between the
 * insets, `CameraFollow.MarginPx` in from its edges: centred there, or with `centre` false moved only as far as it
 * takes to bring them inside. The zoom only ever grows (the view steps back) and only when they cannot fit at the
 * present one, up to `MaxZoom`; the yaw is never touched. Worked on the real projection (a few rounds of "project,
 * measure, move the look point by the ground under the error"), so perspective and the insets are exact, and the
 * result is clamped to the map edge like a drag. The camera is left exactly where it was. `fromZoom` is the zoom to
 * start stepping back from (the present one by default; the player's own, when a placement hands the view back).
 */
export function frameTarget(h: FramingHost, points: readonly { x: number; y: number; z: number }[], centre: boolean, fromZoom = h.zoom): { x: number; z: number; zoom: number } {
  const savedPan = h.pan.clone(), savedZoom = h.zoom, ins = h.insets;
  h.zoom = clampZoom(h, fromZoom);
  h.apply(ins.top, ins.bottom);
  const m = CameraFollow.MarginPx;
  const band: ScreenBox = { minX: m, maxX: h.width - m, minY: ins.top + m, maxY: h.height - ins.bottom - m };
  const bandX = (band.minX + band.maxX) / 2, bandY = (band.minY + band.maxY) / 2;
  const v = new THREE.Vector3();
  for (let round = 0; round < FollowFitRounds && points.length > 0; round++) {
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const p of points) {
      v.set(p.x, p.y, p.z).project(h.camera);
      const sx = ((v.x + 1) / 2) * h.width, sy = ((1 - v.y) / 2) * h.height;
      minX = Math.min(minX, sx); maxX = Math.max(maxX, sx); minY = Math.min(minY, sy); maxY = Math.max(maxY, sy);
    }
    const box = { minX, maxX, minY, maxY };
    h.zoom = Math.min(h.maxZoom, h.zoom * CameraFollow.zoomToFit(box, band));
    const { dx, dy } = CameraFollow.shiftToFit(box, band, centre);
    const from = h.groundAt(bandX, bandY), to = h.groundAt(bandX + dx, bandY + dy);
    if (from !== null && to !== null) { h.pan.add(to.sub(from)); h.pan.y = 0; }
    h.apply(ins.top, ins.bottom);                           // clamped for the band the view is easing to
  }
  const out = { x: h.centre.x + h.pan.x, z: h.centre.z + h.pan.z, zoom: h.zoom };
  h.pan.copy(savedPan); h.zoom = savedZoom;
  h.apply();
  return out;
}

/** `target` as the map edge allows it (as a drag would be clamped), at its zoom; the camera is left as it was. */
function clampedTarget(h: FramingHost, target: { x: number; z: number; zoom: number }): { x: number; z: number; zoom: number } {
  const savedPan = h.pan.clone(), savedZoom = h.zoom;
  h.zoom = clampZoom(h, target.zoom);
  h.pan.set(target.x - h.centre.x, 0, target.z - h.centre.z);
  h.apply(h.insets.top, h.insets.bottom);
  const out = { x: h.centre.x + h.pan.x, z: h.centre.z + h.pan.z, zoom: h.zoom };
  h.pan.copy(savedPan); h.zoom = savedZoom;
  h.apply();
  return out;
}

/**
 * The glide to `target` (clamped to the map edge) over the spring's `smoothTime`, carrying the velocity of the glide
 * under way (`current`). Null when there is none and the target is inside the dead zone of where the view already
 * is, so the camera never twitches for a cell's worth of change.
 */
export function nextFollow(h: FramingHost, target: { x: number; z: number; zoom: number }, smoothTime: number, current: Follow | null): Follow | null {
  target = clampedTarget(h, target);
  const look = lookOf(h);
  const near = Math.hypot(target.x - look.x, target.z - look.z) < CameraFollow.DeadZone && Math.abs(target.zoom - h.zoom) < h.zoom * CameraFollow.ZoomDeadZone;
  if (near && current === null) return null;
  const f = current;
  return { x: target.x, z: target.z, zoom: target.zoom, smoothTime, vx: f?.vx ?? 0, vz: f?.vz ?? 0, vZoom: f?.vZoom ?? 0, age: 0 };
}

/** One spring step of a glide (it re-applies the framing itself). True when the glide is over: settled, or given up. */
export function stepFollow(h: FramingHost, f: Follow, dt: number): boolean {
  f.age += dt;
  const x = CameraFollow.damp(h.centre.x + h.pan.x, f.x, f.vx, f.smoothTime, dt);
  const z = CameraFollow.damp(h.centre.z + h.pan.z, f.z, f.vz, f.smoothTime, dt);
  const k = CameraFollow.damp(h.zoom, f.zoom, f.vZoom, f.smoothTime, dt);
  f.vx = x.velocity; f.vz = z.velocity; f.vZoom = k.velocity;
  h.zoom = clampZoom(h, k.value);
  h.pan.set(x.value - h.centre.x, 0, z.value - h.centre.z);
  h.apply();
  const look = lookOf(h);
  const settled = Math.hypot(f.x - look.x, f.z - look.z) < CameraFollow.SettleCells && Math.abs(f.zoom - h.zoom) < f.zoom * CameraFollow.SettleZoom;
  return settled || f.age > CameraFollow.MaxSeconds;
}
